// Localização: frontend/src/components/Prontuario/FormContaReceberPaciente.jsx
// Formulário simplificado para criar contas a receber do paciente

import React, { useState, useEffect, useRef } from 'react';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';
import LoadingIndicator from '../Common/LoadingIndicator';
import DropdownMenu from '../UI/DropdownMenu';
import { useCategorias, useSubcategorias } from '../../hooks/useFinanceiro';
import financeiroApi from '../../api/financeiroApi';
import { useNotification } from '../../context/NotificationContext';
import { DollarSign, Calendar, FileText, Tag, ChevronDown, Repeat, Receipt, CalendarClock, RotateCw } from 'lucide-react';

// Tipos de lançamento com ícones
const TIPOS_LANCAMENTO = [
    { value: 'unica', label: 'Única', icon: Receipt },
    { value: 'parcelada', label: 'Parcelada', icon: CalendarClock },
    { value: 'recorrente_fixo', label: 'Recorrente Fixo (Mensalidade)', icon: Repeat },
    { value: 'recorrente_variavel', label: 'Recorrente Variável', icon: RotateCw },
];

const FormContaReceberPaciente = ({ isOpen, onClose, pacienteId, pacienteNome, onSuccess }) => {
    const { success, error: showError } = useNotification();
    
    const [formData, setFormData] = useState({
        categoria_id: '',
        subcategoria_id: '',
        descricao: '',
        valor_total: '',
        data_vencimento: '',
        data_competencia: '',
        tipo_lancamento: 'recorrente_fixo', // Padrão para contas de pacientes
        observacoes: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Hooks para buscar dados relacionados
    const { categorias, fetchCategorias, loading: loadingCategorias } = useCategorias();
    const { subcategorias, fetchSubcategorias, loading: loadingSubcategorias } = useSubcategorias();

    // Refs para evitar múltiplas chamadas
    const hasLoadedDataRef = useRef(false);
    const lastCategoriaIdRef = useRef(null);
    const fetchSubcategoriasRef = useRef(fetchSubcategorias);

    // Atualizar ref quando a função mudar
    useEffect(() => {
        fetchSubcategoriasRef.current = fetchSubcategorias;
    }, [fetchSubcategorias]);

    // Carregar dados ao abrir o modal
    useEffect(() => {
        if (isOpen) {
            // Carregar categorias apenas uma vez
            if (!hasLoadedDataRef.current) {
                fetchCategorias({ tipo: 'Receita' }).catch(err => 
                    console.error('[FormContaReceberPaciente] Erro ao carregar categorias:', err)
                );
                hasLoadedDataRef.current = true;
            }

            // Pré-preencher data de competência com data atual
            const hoje = new Date().toISOString().split('T')[0];
            setFormData({
                categoria_id: '',
                subcategoria_id: '',
                descricao: '',
                valor_total: '',
                data_vencimento: '',
                data_competencia: hoje,
                tipo_lancamento: 'recorrente_fixo', // Padrão para contas de pacientes
                observacoes: ''
            });
            setErrors({});
            lastCategoriaIdRef.current = null;
        } else {
            // Resetar flag quando o modal fechar
            hasLoadedDataRef.current = false;
            lastCategoriaIdRef.current = null;
        }
    }, [isOpen, fetchCategorias]);

    // Carregar subcategorias quando categoria mudar
    useEffect(() => {
        if (!isOpen) return;
        
        const currentCategoriaId = formData.categoria_id || null;
        const previousCategoriaId = lastCategoriaIdRef.current;
        
        if (currentCategoriaId !== previousCategoriaId) {
            lastCategoriaIdRef.current = currentCategoriaId;
            
            if (currentCategoriaId) {
                fetchSubcategoriasRef.current({ categoria_id: currentCategoriaId }).catch(err => 
                    console.error('[FormContaReceberPaciente] Erro ao carregar subcategorias:', err)
                );
            } else {
                if (previousCategoriaId !== null) {
                    fetchSubcategoriasRef.current({ categoria_id: null }).catch(err => 
                        console.error('[FormContaReceberPaciente] Erro ao limpar subcategorias:', err)
                    );
                }
            }
        }
    }, [isOpen, formData.categoria_id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Limpar erro do campo
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }

        // Se mudou a categoria, limpar subcategoria
        if (name === 'categoria_id') {
            setFormData(prev => ({ ...prev, subcategoria_id: '' }));
        }
    };

    // Handler para campos que usam DropdownMenu
    const handleDropdownChange = (name, value) => {
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            // Se mudou a categoria, limpar subcategoria
            if (name === 'categoria_id') {
                newData.subcategoria_id = '';
            }
            return newData;
        });
        // Limpar erro do campo
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.descricao?.trim()) {
            newErrors.descricao = 'Descrição é obrigatória';
        }

        if (!formData.valor_total || parseFloat(formData.valor_total) <= 0) {
            newErrors.valor_total = 'Valor total deve ser maior que zero';
        }

        if (!formData.data_vencimento) {
            newErrors.data_vencimento = 'Data de vencimento é obrigatória';
        }

        if (!formData.data_competencia) {
            newErrors.data_competencia = 'Data de competência é obrigatória';
        }

        if (!formData.categoria_id) {
            newErrors.categoria_id = 'Categoria é obrigatória';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const dataToSave = {
                descricao: formData.descricao,
                valor_total: parseFloat(formData.valor_total),
                data_vencimento: formData.data_vencimento,
                data_competencia: formData.data_competencia,
                categoria_id: formData.categoria_id,
                subcategoria_id: formData.subcategoria_id || null,
                paciente_id: pacienteId,
                cliente_fornecedor: pacienteNome || null,
                observacoes: formData.observacoes || null,
                tipo_lancamento: formData.tipo_lancamento || 'recorrente_fixo',
                lancar_quitada: false
            };

            await financeiroApi.contaReceber.create(dataToSave);
            success('Conta a receber criada com sucesso!');
            onClose();
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Erro ao criar conta a receber. Tente novamente.';
            showError(errorMsg);
            console.error('Erro ao salvar conta a receber:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Nova Conta a Receber"
            size="lg"
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Informação do Paciente */}
                {pacienteNome && (
                    <div className="bg-emerald-50 border-[0.5px] border-emerald-200 rounded-lg p-3">
                        <p className="text-sm text-emerald-700">
                            <span className="font-semibold">Paciente:</span> {pacienteNome}
                        </p>
                    </div>
                )}

                {/* Descrição */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FileText className="w-4 h-4 inline mr-1" />
                        Descrição <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="descricao"
                        value={formData.descricao}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border-[0.5px] rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors ${
                            errors.descricao ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Ex: Consulta, Procedimento, etc."
                    />
                    {errors.descricao && (
                        <p className="mt-1 text-sm text-red-500">{errors.descricao}</p>
                    )}
                </div>

                {/* Valor Total e Data de Vencimento */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <DollarSign className="w-4 h-4 inline mr-1" />
                            Valor Total <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            name="valor_total"
                            value={formData.valor_total}
                            onChange={handleChange}
                            step="0.01"
                            min="0.01"
                            className={`w-full px-3 py-2 border-[0.5px] rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors ${
                                errors.valor_total ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="0.00"
                        />
                        {errors.valor_total && (
                            <p className="mt-1 text-sm text-red-500">{errors.valor_total}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Data de Vencimento <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            name="data_vencimento"
                            value={formData.data_vencimento}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 border-[0.5px] rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors ${
                                errors.data_vencimento ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        {errors.data_vencimento && (
                            <p className="mt-1 text-sm text-red-500">{errors.data_vencimento}</p>
                        )}
                    </div>
                </div>

                {/* Data de Competência */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Data de Competência <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        name="data_competencia"
                        value={formData.data_competencia}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border-[0.5px] rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors ${
                            errors.data_competencia ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.data_competencia && (
                        <p className="mt-1 text-sm text-red-500">{errors.data_competencia}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                        Data para cálculo do DRE (Demonstração do Resultado do Exercício)
                    </p>
                </div>

                {/* Tipo de Lançamento - Usando DropdownMenu */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                        <Repeat size={16} className="text-gray-500" />
                        Tipo de Lançamento <span className="text-red-500">*</span>
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                onClick={(e) => e.preventDefault()}
                                className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition"
                            >
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const tipoSelecionado = TIPOS_LANCAMENTO.find(t => t.value === formData.tipo_lancamento);
                                        const Icon = tipoSelecionado?.icon || Repeat;
                                        return (
                                            <>
                                                <Icon size={16} className="text-gray-500" />
                                                <span className="text-gray-700">
                                                    {tipoSelecionado?.label || "Selecione o tipo"}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                                <ChevronDown size={16} className="text-gray-400" />
                            </button>
                        }
                        items={TIPOS_LANCAMENTO.map(t => ({
                            id: t.value,
                            label: t.label,
                            icon: t.icon,
                            iconColor: 'text-gray-600',
                            onClick: () => handleDropdownChange('tipo_lancamento', t.value)
                        }))}
                        position="bottom-left"
                        className="w-full"
                    />
                </div>

                {/* Categoria e Subcategoria - Usando DropdownMenu */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <Tag size={16} className="text-gray-500" />
                            Categoria <span className="text-red-500">*</span>
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className={`w-full flex items-center justify-between gap-2 p-2 border rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition ${
                                        errors.categoria_id ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    disabled={loadingCategorias}
                                >
                                    <div className="flex items-center gap-2">
                                        {loadingCategorias ? (
                                            <>
                                                <LoadingIndicator size="sm" />
                                                <span className="text-gray-500">Carregando...</span>
                                            </>
                                        ) : (() => {
                                            const categoriaSelecionada = categorias?.find(c => String(c.id) === String(formData.categoria_id));
                                            return (
                                                <>
                                                    <Tag size={16} className="text-gray-500" />
                                                    <span className="text-gray-700">
                                                        {categoriaSelecionada?.nome || "Selecione uma categoria..."}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={loadingCategorias ? 
                                [{ id: 'loading', label: 'Carregando...', disabled: true }] :
                                categorias?.map(cat => ({
                                    id: String(cat.id),
                                    label: cat.nome,
                                    icon: Tag,
                                    iconColor: 'text-gray-600',
                                    onClick: () => handleDropdownChange('categoria_id', String(cat.id))
                                })) || []
                            }
                            position="bottom-left"
                            className="w-full"
                        />
                        {errors.categoria_id && (
                            <p className="mt-1 text-sm text-red-500">{errors.categoria_id}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <Tag size={16} className="text-gray-500" />
                            Subcategoria
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={!formData.categoria_id || loadingSubcategorias}
                                >
                                    <div className="flex items-center gap-2">
                                        {loadingSubcategorias ? (
                                            <>
                                                <LoadingIndicator size="sm" />
                                                <span className="text-gray-500">Carregando...</span>
                                            </>
                                        ) : (() => {
                                            const subcategoriaSelecionada = subcategorias?.find(s => String(s.id) === String(formData.subcategoria_id));
                                            return (
                                                <>
                                                    <Tag size={16} className="text-gray-500" />
                                                    <span className="text-gray-700">
                                                        {subcategoriaSelecionada?.nome || "Selecione uma subcategoria..."}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={!formData.categoria_id || loadingSubcategorias ? 
                                [{ id: 'disabled', label: formData.categoria_id ? 'Carregando...' : 'Selecione uma categoria primeiro', disabled: true }] :
                                subcategorias?.map(sub => ({
                                    id: String(sub.id),
                                    label: sub.nome,
                                    icon: Tag,
                                    iconColor: 'text-gray-600',
                                    onClick: () => handleDropdownChange('subcategoria_id', String(sub.id))
                                })) || []
                            }
                            position="bottom-left"
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Observações */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Observações
                    </label>
                    <textarea
                        name="observacoes"
                        value={formData.observacoes}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-3 py-2 border-[0.5px] border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors resize-none"
                        placeholder="Observações adicionais..."
                    />
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t-[0.5px] border-gray-200">
                    <PrimaryButton
                        type="button"
                        onClick={onClose}
                        variant="secondary"
                        disabled={isSubmitting}
                        className="px-4 py-2"
                    >
                        Cancelar
                    </PrimaryButton>
                    <PrimaryButton
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2"
                    >
                        {isSubmitting ? 'Salvando...' : 'Criar Conta'}
                    </PrimaryButton>
                </div>
            </form>
        </Modal>
    );
};

export default FormContaReceberPaciente;

